import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon, Cancel as CancelIcon } from '@vgno/icons';

import { ChipStack } from '../../ChipStack/ChipStack';
import { Button } from '../../Button/Button';
import { Calendar } from '../../Calendar/Calendar';
import { Overlay } from '../../Overlay/Overlay';
import { useViewportType } from '../../utils/useViewportType';

import type { ChipStackProps } from '../../ChipStack/ChipStack';
import type { KeyboardEventHandler, VFC } from 'react';

import * as styles from './AssetsScheduleHeader.module.scss';

export interface AssetsScheduleHeaderProps {
  chips: ChipStackProps['chips'];
  isChipStackLoading?: boolean;
  datesToMark?: string[];
  selectedDate?: Date;
  selection?: string[];
  onSelectionChange?: (ids: string[]) => void;
  onDateChange?: (value: Date) => void;
  title: string;
  onCloseButtonClick?: () => void;
}

const AssetsScheduleHeader: VFC<AssetsScheduleHeaderProps> = ({
  chips,
  isChipStackLoading,
  datesToMark,
  selectedDate,
  onDateChange,
  selection,
  onSelectionChange,
  title,
  onCloseButtonClick,
}) => {
  const { isMobile } = useViewportType();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleDateChange = useCallback(
    (value: Date) => {
      if (onDateChange) {
        onDateChange(value);
      }

      setIsCalendarOpen(false);
    },
    [onDateChange]
  );

  const handleCalendarButtonClick = useCallback(() => {
    setIsCalendarOpen((prevIsCalendarOpen) => !prevIsCalendarOpen);
  }, []);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        setIsCalendarOpen(false);
      }
    },
    []
  );

  const handleBackdropClick = useCallback(() => {
    setIsCalendarOpen(false);
  }, []);

  useEffect(() => {
    if (!isCalendarOpen || !calendarContainerRef.current) {
      return;
    }

    const handler = (e: DocumentEventMap['click']) => {
      if (!calendarContainerRef.current!.contains(e.target as Node)) {
        e.preventDefault();

        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('click', handler, true);

    return () => document.removeEventListener('click', handler, true);
  }, [isCalendarOpen]);

  return (
    <div className={styles.header}>
      <div className={styles.row}>
        <h1 className={styles.title} title={title}>
          {title}
        </h1>
        <Button
          variant="contained"
          size="medium"
          onClick={handleCalendarButtonClick}
          data-testId="open-calendar"
        >
          <CalendarIcon />
        </Button>
        {onCloseButtonClick && (
          <Button
            variant="contained"
            size="medium"
            onClick={onCloseButtonClick}
            aria-label={t('Close')}
          >
            <CancelIcon />
            {t('Close')}
          </Button>
        )}
      </div>
      {(chips.length > 0 || isChipStackLoading) && (
        <div className={styles.row}>
          <ChipStack
            chips={chips}
            isLoading={isChipStackLoading}
            selection={selection}
            onSelectionChange={onSelectionChange}
          />
        </div>
      )}
      {!isMobile && isCalendarOpen && (
        <div
          className={styles.calendarContainer}
          onKeyDown={handleKeyDown}
          role="presentation"
          ref={calendarContainerRef}
        >
          <Calendar
            datesToMark={datesToMark}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
          />
        </div>
      )}
      {isMobile && (
        <Overlay
          isOpen={isCalendarOpen}
          forceRender
          isTransparent
          onBackdropClick={handleBackdropClick}
          className={styles.calendarOverlay}
        >
          <Calendar
            datesToMark={datesToMark}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
          />
        </Overlay>
      )}
    </div>
  );
};

export { AssetsScheduleHeader };
